/**
 * NewsItemRow — one feed item (shared by the /news page, the Home strip and
 * the account-page section). Pure presentational; chips call back so each
 * host decides what a click means (filter, navigate, nothing).
 */

import React, { useState, useCallback } from 'react';
import { ExternalLink, Building2, User, Bookmark, BookOpen, Sparkles } from 'lucide-react';
import { NEWS_CATEGORY_LABEL } from '@techstack/core';
import apiClient from '../../lib/apiClient';

export function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: days > 300 ? 'numeric' : undefined });
}

const TIER_LABEL = {
  ca_it_trade: 'CA IT press', ca_politics: 'Capitol press', ca_newsroom: 'State newsroom',
  agency_newsroom: 'Agency newsroom', national_govtech: 'Gov-tech press', security: 'Security press',
  local_gov: 'Local gov', sector: 'Sector press', internal: 'TekStak signal', industry: 'Industry press', assistant: 'Assistant',
};

/**
 * The article, opened in place.
 *
 * `has_body` comes from the list; the TEXT does not, because a body runs to
 * 12,000 characters and a thirty-item feed would ship a third of a megabyte
 * nobody has asked to read. So the row fetches on expand and keeps it.
 *
 * Summarising is a separate, deliberate press. It is the only thing in the
 * news pipeline that spends money — the server meters it and refuses when the
 * caller is over budget — so it is never automatic, never on expand, and an
 * article that already has one returns it without another charge.
 */
function ArticleReader({ item }) {
  const [open, setOpen] = useState(false);
  const [article, setArticle] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (article) return article;
    setBusy('read');
    setError(null);
    try {
      const r = await apiClient.get(`/api/v1/department-news/items/${item.id}/article`);
      setArticle(r);
      if (r?.summary) setSummary(r.summary);
      return r;
    } catch (e) {
      setError(e.message || 'Could not load the article.');
      return null;
    } finally {
      setBusy(null);
    }
  }, [article, item.id]);

  const toggle = useCallback(async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    await load();
  }, [open, load]);

  const summarize = useCallback(async () => {
    setBusy('summary');
    setError(null);
    try {
      const r = await apiClient.post(`/api/v1/department-news/items/${item.id}/summarize`, {});
      setSummary(r.summary);
      setOpen(true);
    } catch (e) {
      // The two refusals a reader can act on say so plainly; everything else
      // is the generic message. 402 is the budget guard, which fails closed.
      setError(
        e.code === 'AI_BUDGET_EXCEEDED' ? 'You are over your AI budget for this month.'
          : e.code === 'NO_BODY' ? 'There is no stored text for this article to summarise.'
            : e.message || 'Could not summarise this article.',
      );
    } finally {
      setBusy(null);
    }
  }, [item.id]);

  if (!item.has_body) return null;

  return (
    <div className="nf-article">
      <div className="nf-article-actions">
        <button type="button" className="nf-act" onClick={toggle} aria-expanded={open}>
          <BookOpen size={12} /> {open ? 'Hide' : 'Read'}
        </button>
        {!summary && (
          <button
            type="button"
            className="nf-act nf-act--ai"
            onClick={summarize}
            disabled={busy === 'summary'}
            title="Generates a summary now. This is the one thing here that costs money."
          >
            <Sparkles size={12} /> {busy === 'summary' ? 'Summarising…' : 'Summarise'}
          </button>
        )}
      </div>
      {error && <p className="nf-article-err">{error}</p>}
      {summary && (
        <p className="nf-article-summary">
          <span className="nf-article-tag">Summary</span>
          {summary}
        </p>
      )}
      {open && (
        busy === 'read'
          ? <p className="nf-article-body nf-article-body--wait">Loading the article…</p>
          : article?.body
            ? <div className="nf-article-body">{article.body.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}</div>
            : !error && <p className="nf-article-body nf-article-body--wait">No stored text for this one.</p>
      )}
    </div>
  );
}

export default function NewsItemRow({ item, compact = false, onDepartment, onContact, onCategory }) {
  const internal = !!item.internal_ref;
  const depts = item.departments || [];
  const contacts = item.contacts || [];
  return (
    <article
      className={`nf-item nf-lane--${item.lane || 'department'} ${internal ? 'nf-item--internal' : ''} ${compact ? 'nf-item--compact' : ''}`}
      // The lane (state / department / industry) is the feed's top-level
      // split and was carried on every item while being visible on only one
      // of the three — `industry` got a chip, the other two got nothing. A
      // stripe says it on every card without spending a chip slot, which in
      // compact mode is the scarcest thing on the row.
      title={`${item.lane || 'department'} lane`}
    >
      <div className="nf-item-head">
        {/* A reference to the publisher's image, never a copy of it. About a
            third of sources ship none, so the row must read without it — which
            is why this sits beside the title rather than above it. */}
        {item.image_url && (
          /*
           * The PICTURE opens the story too.
           *
           * Ryan: "If I click on a news article I want it to open in a new
           * tab." The headline already did — anchor, target=_blank,
           * rel=noopener — on every item. What did not was everything else,
           * and in a feed the picture and the standfirst are most of the
           * article: clicking them did nothing at all, which reads as broken
           * rather than as "that part isn't a link".
           *
           * One MEANINGFUL link per card stays the rule, so this and the
           * standfirst are hidden from the accessibility tree and taken out of
           * the tab order. A screen reader hears one link to this story, not
           * three; a pointer can press any of it.
           */
          item.url ? (
            <a
              className="nf-item-media"
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={-1}
              aria-hidden="true"
            >
              <img
                className="nf-item-thumb"
                src={item.image_url}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </a>
          ) : (
            <img
              className="nf-item-thumb"
              src={item.image_url}
              alt=""
              loading="lazy"
              // A dead or hotlink-blocked image must not leave a broken icon in
              // the feed; the row simply loses its picture.
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )
        )}
        {item.url ? (
          <a className="nf-item-title" href={item.url} target="_blank" rel="noopener noreferrer">
            {item.title} <ExternalLink size={12} className="nf-item-ext" />
          </a>
        ) : (
          <span className="nf-item-title">{item.title}</span>
        )}
      </div>
      <div className="nf-item-meta">
        {/* The publisher's initial, as an identity mark. A feed people stay in
            is one where the source is recognisable BEFORE the headline is
            read — the same job an avatar does in any social feed. It is a
            letter we render, never the publisher's logo: that would be their
            mark on our surface, and a third of sources ship no image anyway. */}
        {item.publisher && (
          <span className="nf-item-mark" aria-hidden="true">{item.publisher.trim().charAt(0)}</span>
        )}
        <span className="nf-item-when">{fmtWhen(item.published_at || item.fetched_at)}</span>
        {item.publisher && <span className="nf-item-pub">{item.publisher}</span>}
        <span className={`nf-tier nf-tier--${item.source_tier}`} title={item.source_name}>
          {internal ? <Bookmark size={11} /> : null}{TIER_LABEL[item.source_tier] || item.source_tier}
        </span>
        {item.submitted_by && (
          <span className="nf-tier nf-tier--assistant" title={`Filed through ${item.submitted_via || 'an assistant'}`}>
            submitted by {item.submitted_by}
          </span>
        )}
        {item.lane === 'industry' && <span className="nf-tier nf-tier--industry">industry</span>}
      </div>
      {/* The strip used to throw the summary away. It is the single biggest
          reason the feed did not hold anyone: the rows carry 165-389
          characters of real standfirst and showed a bare headline instead, so
          every story asked the reader to leave in order to find out what it
          said. Shown in both modes now; the strip clamps it to two lines. */}
      {item.summary && (item.url ? (
        <a
          className="nf-item-summary nf-item-summary--link"
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={-1}
          aria-hidden="true"
        >
          {item.summary}
        </a>
      ) : (
        <p className="nf-item-summary">{item.summary}</p>
      ))}
      <ArticleReader item={item} />
      {(depts.length > 0 || contacts.length > 0 || (!compact && item.categories?.length > 0)) && (
        <div className="nf-item-chips">
          {depts.slice(0, compact ? 2 : 6).map((d) => (
            onDepartment ? (
              <button
                key={d.department_id}
                type="button"
                className={`nf-chip nf-chip--dept ${d.confidence < 60 ? 'nf-chip--weak' : ''}`}
                title={`${d.name || ''} · ${d.match_kind} match, ${d.confidence}% confidence`}
                onClick={() => onDepartment(d)}
              >
                <Building2 size={11} /> {d.acronym || d.name}
              </button>
            ) : (
              <span
                key={d.department_id}
                className={`nf-chip nf-chip--dept nf-chip--static ${d.confidence < 60 ? 'nf-chip--weak' : ''}`}
                title={`${d.name || ''} · ${d.match_kind} match, ${d.confidence}% confidence`}
              >
                <Building2 size={11} /> {d.acronym || d.name}
              </span>
            )
          ))}
          {/* A chip only becomes a button where the host gave it somewhere to
              go. These were <button> unconditionally with `onClick={() =>
              onContact?.(c)}`, so on every surface that passed no handler —
              the account News tab and the board widget among them — they
              looked, hovered and focused like controls and did nothing. */}
          {contacts.slice(0, compact ? 1 : 4).map((c) => (
            onContact ? (
              <button
                key={c.contact_id}
                type="button"
                className="nf-chip nf-chip--contact"
                title={c.title ? `${c.name} — ${c.title}` : c.name}
                onClick={() => onContact(c)}
              >
                <User size={11} /> {c.name}
              </button>
            ) : (
              <span
                key={c.contact_id}
                className="nf-chip nf-chip--contact nf-chip--static"
                title={c.title ? `${c.name} — ${c.title}` : c.name}
              >
                <User size={11} /> {c.name}
              </span>
            )
          ))}
          {!compact && (item.categories || []).filter((c) => c !== 'operations').map((c) => (
            <button key={c} type="button" className={`nf-chip nf-chip--cat nf-cat--${c}`} onClick={() => onCategory?.(c)}>
              {NEWS_CATEGORY_LABEL[c] || c}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}
